import { bindEvents, offset } from '../../assets/js/helpers';

import AnimationController from '../../assets/js/controller/animation';
import AudioController from '../../assets/js/controller/audio';
import OverlayController from '../../assets/js/controller/overlay';


const selector = 'video-player';

export default class VideoPlayer {

	constructor(section) {
		this.el = {
			section,
			video: section.querySelector('video'),
			timeCurrent: section.querySelector(`.${selector}__time-current`),
			timeDuration: section.querySelector(`.${selector}__time-duration`),
			toggleMute: section.querySelector(`.${selector}__toggle-mute`),
			progress: section.querySelector(`.${selector}__progress`),
			progressIndicator: section.querySelector(`.${selector}__progress-indicator`)
		};

		this.duration = 0;
		this.currentTime = 0;
		this.progress = 0;
		this.formattedTime = '';

		this.width = 0;
		this.offset = 0;

		this.muted = false;
		this.seeking = false;

		this.animation = null;

		this.events = bindEvents(this);

		this.addEventListeners();
		this.measure();
	}

	static get selector() {
		return selector;
	}

	measure() {
		this.width = this.el.section.offsetWidth;
		this.offset = offset.left(this.el.section);
	}

	formatTime(time) {
		const numbers = parseInt(time, 10);

		let hours = Math.floor(numbers / 3600),
			minutes = Math.floor((numbers - (hours * 3600)) / 60),
			seconds = numbers - (hours * 3600) - (minutes * 60);

		if (hours < 10) hours = `0${hours}`;
		if (minutes < 10) minutes = `0${minutes}`;
		if (seconds < 10) seconds = `0${seconds}`;

		return hours === '00' ? `${minutes}:${seconds}` : `${hours}:${minutes}:${seconds}`;
	}

	update() {
		if (this.currentTime !== this.el.video.currentTime) {
			this.currentTime = this.el.video.currentTime;
			this.progress = this.currentTime / this.duration;
			this.formattedTime = this.formatTime(this.currentTime);
			return true;
		}
	}

	render() {
		this.el.timeCurrent.innerText = this.formattedTime;
		this.el.progressIndicator.style.transform = `scaleX(${this.progress})`;
	}

	start() {
		this.animation = AnimationController.set({
			update: this.update.bind(this),
			render: this.render.bind(this)
		}, 'video player');
	}

	pause() {
		AnimationController.clear(this.animation);
	}

	_onWindowLoad() {
		this.measure();
	}

	_onWindowResize() {
		this.measure();
	}

	_onDocumentMouseMove(e) {
		if (this.seeking) {
			e.preventDefault();
			this.el.video.currentTime = (e.pageX - this.offset) / this.width * this.duration;
		}
	}

	_onDocumentMouseUp() {
		if (this.seeking) {
			this.seeking = false;
		}
	}

	_onSectionOpen() {
		this.removeEventListeners();
		this.addEventListeners();
		this.el.video.play();
	}

	_onSectionClose() {
		this.el.video.pause();
	}

	_onVideoLoadedMetaData() {
		this.duration = this.el.video.duration;
		this.el.timeDuration.innerText = this.formatTime(this.duration);
	}

	_onVideoPlay() {
		if (!this.duration) this._onVideoLoadedMetaData();
		this.start();
		this.el.section.classList.add('is-playing');
		AudioController.volume('background', 0);
	}

	_onVideoPause() {
		this.pause();
		this.el.section.classList.remove('is-playing');
		AudioController.volume('background', 1);
	}

	_onVideoEnded() {
		OverlayController.close('videoPlayer');
	}

	_onVideoVolumeChange(e) {
		if (!e.target.volume) {
			this.muted = true;
			this.el.toggleMute.classList.add('is-muted');
		} else {
			this.muted = false;
			this.el.toggleMute.classList.remove('is-muted');
		}
	}

	_onVideoClick() {
		this.el.video.paused ? this.el.video.play() : this.el.video.pause();
	}

	_onToggleMuteClick(e) {
		e.preventDefault();
		this.el.video.volume = !this.muted ? 0 : 1;
	}

	_onProgressMouseDown(e) {
		e.preventDefault();
		this.seeking = true;
		this.el.video.currentTime = e.pageX / window.innerWidth * this.duration;
	}

	addEventListeners() {
		window.addEventListener('load', this.events._onWindowLoad);
		window.addEventListener('resize', this.events._onWindowResize);

		document.addEventListener('mousemove', this.events._onDocumentMouseMove);
		document.addEventListener('mouseup', this.events._onDocumentMouseUp);

		this.el.section.addEventListener('open', this.events._onSectionOpen);
		this.el.section.addEventListener('close', this.events._onSectionClose);
		this.el.video.addEventListener('loadedmetadata', this.events._onVideoLoadedMetaData);
		this.el.video.addEventListener('play', this.events._onVideoPlay);
		this.el.video.addEventListener('pause', this.events._onVideoPause);
		this.el.video.addEventListener('ended', this.events._onVideoEnded);
		this.el.video.addEventListener('volumechange', this.events._onVideoVolumeChange);
		this.el.video.addEventListener('click', this.events._onVideoClick);
		this.el.toggleMute.addEventListener('click', this.events._onToggleMuteClick);
		this.el.progress.addEventListener('mousedown', this.events._onProgressMouseDown);
	}

	removeEventListeners() {
		window.removeEventListener('load', this.events._onWindowLoad);
		window.removeEventListener('resize', this.events._onWindowResize);

		document.removeEventListener('mousemove', this.events._onDocumentMouseMove);
		document.removeEventListener('mouseup', this.events._onDocumentMouseUp);

		this.el.section.removeEventListener('open', this.events._onSectionOpen);
		this.el.section.removeEventListener('close', this.events._onSectionClose);
		this.el.video.removeEventListener('loadedmetadata', this.events._onVideoLoadedMetaData);
		this.el.video.removeEventListener('play', this.events._onVideoPlay);
		this.el.video.removeEventListener('pause', this.events._onVideoPause);
		this.el.video.removeEventListener('ended', this.events._onVideoEnded);
		this.el.video.removeEventListener('volumechange', this.events._onVideoVolumeChange);
		this.el.video.removeEventListener('click', this.events._onVideoClick);
		this.el.toggleMute.removeEventListener('click', this.events._onToggleMuteClick);
		this.el.progress.removeEventListener('mousedown', this.events._onProgressMouseDown);
	}

	destroy() {
		this.pause();
		this.removeEventListeners();
	}

}
